import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { api } from '@/utils/api';
import { TrendingUp, TrendingDown, AlertTriangle, ArrowUp, ArrowDown } from 'lucide-react';
import Layout from '@/components/Layout';

const insightColors = {
  underpriced: { bg: 'bg-green-50 border-green-200', icon: 'text-green-600', badge: 'bg-green-100 text-green-800' },
  overpriced: { bg: 'bg-red-50 border-red-200', icon: 'text-red-600', badge: 'bg-red-100 text-red-800' },
  vacancy_risk: { bg: 'bg-yellow-50 border-yellow-200', icon: 'text-yellow-600', badge: 'bg-yellow-100 text-yellow-800' },
  high_performer: { bg: 'bg-blue-50 border-blue-200', icon: 'text-blue-600', badge: 'bg-blue-100 text-blue-800' },
  low_performer: { bg: 'bg-orange-50 border-orange-200', icon: 'text-orange-600', badge: 'bg-orange-100 text-orange-800' }
};

const insightIcons = {
  underpriced: ArrowUp,
  overpriced: ArrowDown,
  vacancy_risk: AlertTriangle,
  high_performer: TrendingUp,
  low_performer: TrendingDown
};

export default function Insights() {
  const { t, i18n } = useTranslation();
  const [insights, setInsights] = useState([]);
  const [units, setUnits] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [insightsRes, unitsRes, propsRes] = await Promise.all([
        api.getRevenueInsights(), api.getUnits(), api.getProperties()
      ]);
      setInsights(insightsRes.data);
      setUnits(unitsRes.data);
      setProperties(propsRes.data);
    } catch (error) { console.error('Failed:', error); }
    finally { setLoading(false); }
  };

  const getUnitLabel = (id) => {
    const unit = units.find(u => u.id === id);
    return unit ? `Unit ${unit.unit_number} (${unit.unit_type})` : '-';
  };

  const getPropertyName = (id) => properties.find(p => p.id === id)?.name || '-';

  const isArabic = i18n.language === 'ar';

  const underpricedCount = insights.filter(i => i.insight_type === 'underpriced').length;
  const overpricedCount = insights.filter(i => i.insight_type === 'overpriced').length;
  const vacancyRiskCount = insights.filter(i => i.insight_type === 'vacancy_risk').length;

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">{t('insights')}</h1>
          <p className="text-gray-500 mt-1">Rule-based revenue optimization recommendations for your portfolio</p>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="border-green-200 bg-green-50">
            <CardContent className="pt-6">
              <ArrowUp className="h-5 w-5 text-green-600 mb-2" />
              <div className="text-2xl font-bold text-green-700">{underpricedCount}</div>
              <div className="text-sm text-green-600">{t('underpriced')} Units</div>
              <p className="text-xs text-green-500 mt-1">Potential for rent increase</p>
            </CardContent>
          </Card>
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <ArrowDown className="h-5 w-5 text-red-600 mb-2" />
              <div className="text-2xl font-bold text-red-700">{overpricedCount}</div>
              <div className="text-sm text-red-600">{t('overpriced')} Units</div>
              <p className="text-xs text-red-500 mt-1">May lead to vacancy</p>
            </CardContent>
          </Card>
          <Card className="border-yellow-200 bg-yellow-50">
            <CardContent className="pt-6">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mb-2" />
              <div className="text-2xl font-bold text-yellow-700">{vacancyRiskCount}</div>
              <div className="text-sm text-yellow-600">{t('vacancyRisk')}</div>
              <p className="text-xs text-yellow-500 mt-1">Currently vacant units</p>
            </CardContent>
          </Card>
        </div>

        {/* Insights List */}
        {loading ? (
          <div className="text-center py-12">Loading insights...</div>
        ) : insights.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <TrendingUp className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-500">No insights yet</h3>
              <p className="text-sm text-gray-400 mt-1">Add more properties and units to generate revenue insights</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {insights.map((insight, index) => {
              const colors = insightColors[insight.insight_type] || insightColors.vacancy_risk;
              const Icon = insightIcons[insight.insight_type] || AlertTriangle;
              return (
                <Card key={index} className={`border ${colors.bg}`}>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-lg ${colors.badge}`}>
                        <Icon className="h-6 w-6" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors.badge}`}>
                            {t(insight.insight_type === 'vacancy_risk' ? 'vacancyRisk' : insight.insight_type)}
                          </span>
                          <span className="text-xs text-gray-400">
                            Confidence: {Math.round(insight.confidence_score * 100)}%
                          </span>
                        </div>
                        <h3 className="font-semibold text-gray-900">
                          {getUnitLabel(insight.unit_id)} - {getPropertyName(insight.property_id)}
                        </h3>
                        <p className="text-sm text-gray-600 mt-1">
                          {isArabic && insight.reasoning_ar ? insight.reasoning_ar : insight.reasoning}
                        </p>
                        <div className="flex gap-6 mt-3 text-sm">
                          <div>
                            <span className="text-gray-500">{t('currentRent')}:</span>{' '}
                            <span className="font-semibold">{insight.current_rent?.toLocaleString()} SAR</span>
                          </div>
                          {insight.suggested_rent && (
                            <div>
                              <span className="text-gray-500">{t('suggestedRent')}:</span>{' '}
                              <span className="font-semibold text-green-600">{insight.suggested_rent?.toLocaleString()} SAR</span>
                            </div>
                          )}
                          {insight.suggested_rent && insight.current_rent && (
                            <div>
                              <span className="text-gray-500">Difference:</span>{' '}
                              <span className={`font-semibold ${insight.suggested_rent > insight.current_rent ? 'text-green-600' : 'text-red-600'}`}>
                                {insight.suggested_rent > insight.current_rent ? '+' : ''}{Math.round(insight.suggested_rent - insight.current_rent)} SAR
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
